interface EventHandlerParams {
    visibilityState: DocumentVisibilityState;
    event: Event;
    dwellTime: number;
}
type EventHandler = (params: EventHandlerParams) => void;
interface ListenerOptions {
    once?: boolean;
}
interface EventSubscriber extends ListenerOptions {
    handler: EventHandler;
}

let eventSubscribers: Array<EventSubscriber> = [];

// clock the last transitioned time
let lastTransitionedAt = 0;

// browser specific event name property
let visibilityChangeProperty: string;

/**
 * Get browser specific event-name for registering the listener
 */
const getVisibilityChangeProperty = () => {
    if (visibilityChangeProperty) {
        return visibilityChangeProperty;
    }

    if (typeof document.hidden !== 'undefined') {
        // Opera 12.10 and Firefox 18 and later support
        visibilityChangeProperty = 'visibilitychange';

        // @ts-ignore-next-line
    } else if (typeof document.msHidden !== 'undefined') {
        visibilityChangeProperty = 'msvisibilitychange';

        // @ts-ignore-next-line
    } else if (typeof document.webkitHidden !== 'undefined') {
        visibilityChangeProperty = 'webkitvisibilitychange';
    } else {
        // default
        visibilityChangeProperty = 'visibilitychange';
    }

    return visibilityChangeProperty;
};

const onVisibilityChangeEvent = (event: Event) => {
    const now = Date.now();

    const dwellTime = now - lastTransitionedAt;
    lastTransitionedAt = now;

    eventSubscribers.forEach(subscriber =>
        subscriber.handler({ visibilityState: document.visibilityState, event, dwellTime })
    );

    // remove any one-time listeners
    eventSubscribers = eventSubscribers.filter(subscriber => !subscriber.once);
};

const subscribeToVisibilityChangeEvent = (handler: EventHandler, options: ListenerOptions) => {
    if (eventSubscribers.length === 0) {
        document.addEventListener(getVisibilityChangeProperty(), onVisibilityChangeEvent, {
            capture: true,
        });

        // clock the time of transition if not set for the session
        if (!lastTransitionedAt) {
            lastTransitionedAt = Date.now();
        }
    }

    eventSubscribers.push({ handler, ...options });
};

export const removeVisibilityChangeListener = (handler: EventHandler) => {
    if (eventSubscribers.length) {
        eventSubscribers = eventSubscribers.filter(subscriber => subscriber.handler !== handler);

        if (eventSubscribers.length === 0) {
            document.removeEventListener(getVisibilityChangeProperty(), onVisibilityChangeEvent);
        }
    }
};

/**
 * Add listener to page visibility change event
 * Reuses a single attached event handle for the visibility event, rather than attaching
 * a new handler to the DOM for each use case.
 */
export const addVisibilityChangeListener = (handler: EventHandler, options?: ListenerOptions) => {
    subscribeToVisibilityChangeEvent(handler, options);

    return () => removeVisibilityChangeListener(handler);
};
